import { getAvatarById, getBackgroundById } from '../../assets/registry'
import { DEFAULT_OUTPUT } from './helpers'

export const isSafeUrl = (src) => {
  if (!src || typeof src !== 'string') return false
  if (src.startsWith('data:') || src.startsWith('blob:')) return true
  try {
    const resolved = new URL(src, window.location.href)
    return resolved.origin === window.location.origin
  } catch {
    return false
  }
}

// Simple memoization to avoid reloading the same URL repeatedly during resizes
const _imageCache = new Map()

export const loadSafeImage = async (src) => {
  if (!src) return { img: null, safe: false }
  const cached = _imageCache.get(src)
  if (cached && cached.img) return cached

  // Coalesce concurrent loads for the same src
  if (cached && cached.promise) return cached.promise

  const promise = (async () => {
    // console.debug && console.debug('[loadSafeImage] start', src)
    const img = new Image()
    if (!src.startsWith('data:') && !src.startsWith('blob:')) {
      img.crossOrigin = 'anonymous'
    }

    const safe = await new Promise((resolve) => {
      img.onload = async () => {
        // console.debug && console.debug('[loadSafeImage] loaded', src)
        if (isSafeUrl(src)) {
          // console.debug && console.debug('[loadSafeImage] url origin matches, marking safe')
          return resolve(true)
        }
        try {
          const mod = await import('./canvasAdapter')
          const createCanvasAdapter =
            mod.createCanvasAdapter || (mod.default && mod.default.createCanvasAdapter)
          if (!createCanvasAdapter) throw new Error('createCanvasAdapter not found')
          const adapter = createCanvasAdapter()
          const testCanvas = adapter.createDOMCanvas(2, 2)
          const testCtx = adapter.getContext(testCanvas, '2d')
          testCtx.drawImage(img, 0, 0, 2, 2)
          testCtx.getImageData(0, 0, 1, 1)
          //  console.debug && console.debug('[loadSafeImage] canvas readback OK, marking safe')
          resolve(true)
        } catch (err) {
          console.debug &&
            console.debug(
              '[loadSafeImage] canvas readback failed, image not safe',
              err && err.message
            )
          resolve(false)
        }
      }
      img.onerror = (e) => {
        console.debug && console.debug('[loadSafeImage] image load error', src, e && e.message)
        resolve(false)
      }
      img.src = src
    })

    const result = { img, safe }
    _imageCache.set(src, result)
    return result
  })()

  _imageCache.set(src, { promise })
  return promise
}

export const resolveAvatarSrc = (avatarId, avatarSrcOverride = null) => {
  if (avatarSrcOverride) return avatarSrcOverride
  const avatar = getAvatarById(avatarId)
  if (avatar?.src) return avatar.src
  if (typeof avatarId === 'string' && avatarId.length > 0) {
    // Unknown/custom ids (e.g. character avatars) must not fall back to a built-in image.
    return null
  }
  return null
}

export const resolveBackgroundSrc = (backgroundId, backgroundSrcOverride = null) => {
  const background = getBackgroundById(backgroundId)
  return backgroundSrcOverride || background?.src || null
}

export const resolveOutputDimensions = (
  output,
  sourceWidth,
  sourceHeight,
  explicitSize = false
) => {
  if (explicitSize) {
    const requestedWidth = Number(output?.width)
    const requestedHeight = Number(output?.height)
    if (
      Number.isFinite(requestedWidth) &&
      requestedWidth > 0 &&
      Number.isFinite(requestedHeight) &&
      requestedHeight > 0
    ) {
      return {
        width: Math.max(160, Math.round(requestedWidth)),
        height: Math.max(120, Math.round(requestedHeight))
      }
    }
  }

  const vw = Number(sourceWidth) || 0
  const vh = Number(sourceHeight) || 0
  if (!(vw > 0 && vh > 0)) {
    return {
      width: DEFAULT_OUTPUT.width,
      height: DEFAULT_OUTPUT.height
    }
  }

  const quality = String(
    output?.quality || output?.avatarCutoutQuality || output?.cutoutQuality || ''
  ).toLowerCase()
  const qualityLongEdge =
    quality === 'high'
      ? 1280
      : quality === 'balanced'
        ? 960
        : quality === 'fast'
          ? 640
          : Math.max(DEFAULT_OUTPUT.width, DEFAULT_OUTPUT.height)
  const srcLongEdge = Math.max(vw, vh)
  const longEdge = Math.max(320, Math.min(srcLongEdge, qualityLongEdge))
  if (vw >= vh) {
    return {
      width: longEdge,
      height: Math.max(120, Math.round((longEdge * vh) / vw))
    }
  }

  return {
    width: Math.max(120, Math.round((longEdge * vw) / vh)),
    height: longEdge
  }
}

export const waitForVideoMetadata = async (video, timeoutMs = 1200) => {
  if (video?.videoWidth > 0 && video?.videoHeight > 0) return
  await new Promise((resolve) => {
    if (!video) {
      resolve()
      return
    }
    let settled = false
    const done = () => {
      if (settled) return
      settled = true
      video.removeEventListener('loadedmetadata', done)
      video.removeEventListener('resize', done)
      clearTimeout(timer)
      resolve()
    }
    const timer = setTimeout(done, timeoutMs)
    video.addEventListener('loadedmetadata', done, { once: true })
    video.addEventListener('resize', done, { once: true })
  })
}

export default {
  isSafeUrl,
  loadSafeImage,
  resolveAvatarSrc,
  resolveBackgroundSrc,
  resolveOutputDimensions,
  waitForVideoMetadata
}

import { buildAvatarRig } from './rig'
import { buildAvatarHeadLayer } from './headBuilder'
import { buildFeatureWrapProfile, buildExpressionWeights } from './faceMath'

export async function refreshBackgroundAsset({
  settings,
  currentBackgroundSrc = null,
  force = false,
  debug = null
} = {}) {
  const nextBackgroundSrc = resolveBackgroundSrc(settings.backgroundId, settings.backgroundSrc)
  if (!force && nextBackgroundSrc === currentBackgroundSrc) return { changed: false }
  const bgImage = nextBackgroundSrc
    ? await loadSafeImage(nextBackgroundSrc)
    : { img: null, safe: false }
  debug && debug && debug('[refreshBackgroundAsset] loaded', !!bgImage?.img)
  return { changed: true, nextBackgroundSrc, bgImage }
}

export async function refreshAvatarAssets({
  settings,
  currentAvatarSrc = null,
  currentAvatarHair = null,
  faceLandmarkerImage = null,
  hairSegmenterImage = null,
  force = false,
  debug = null
} = {}) {
  const nextAvatarSrc = resolveAvatarSrc(settings.avatarId, settings.avatarSrc)
  const hairChanged = currentAvatarHair !== settings.avatarHair
  if (!force && !hairChanged && nextAvatarSrc === currentAvatarSrc) return { changed: false }
  const nextImage = nextAvatarSrc ? await loadSafeImage(nextAvatarSrc) : { img: null, safe: false }
  debug &&
    debug(
      '[refreshAvatarAssets] loaded avatar - img:%s safe:%s',
      !!nextImage?.img,
      !!nextImage?.safe
    )
  if (!nextImage?.img || !nextImage.safe) return { changed: false }

  // Build rig and head layers without mutating engine state
  const nextRig = await buildAvatarRig({
    avatarImage: nextImage,
    faceLandmarkerImage,
    debug,
    yawRecenter: settings?.avatarYawRecenter !== false,
    yawRecenterStrength: settings?.avatarYawRecenterStrength,
    yawRecenterMaxDeg: settings?.avatarYawRecenterMaxDeg
  })
  if (!nextRig) {
    debug && debug('[refreshAvatarAssets] buildAvatarRig failed; using fallback layers')
    const featureWrapProfile = null
    const nextLayers = { _fallback: true }
    return {
      changed: true,
      nextAvatarSrc,
      nextImage,
      nextRig: null,
      nextLayers,
      featureWrapProfile
    }
  }
  // Optionally normalize the mouth to be closed at rest by shrinking the base mouth radius
  try {
    const restScaleRaw = Number(settings?.mouthRestScale)
    const useCloseFlag = settings?.mouthRestClose !== false
    const hasScale = Number.isFinite(restScaleRaw) && restScaleRaw >= 0
    const restScale =
      useCloseFlag && !hasScale ? 0.12 : hasScale ? Math.max(0, Math.min(1, restScaleRaw)) : null
    if (restScale !== null && nextRig && typeof nextRig.mouthRadiusY === 'number') {
      const newMouthRadiusY = nextRig.mouthRadiusY * restScale
      // Recompute expression weights so downstream warps use the updated rest radius
      const expr = buildExpressionWeights(nextRig.canonical, {
        mouthCenter: nextRig.mouthCenter,
        mouthRadiusX: nextRig.mouthRadiusX,
        mouthRadiusY: newMouthRadiusY,
        leftEyeCenter: nextRig.leftEyeCenter,
        leftEyeRadiusX: nextRig.leftEyeRadiusX,
        leftEyeRadiusY: nextRig.leftEyeRadiusY,
        rightEyeCenter: nextRig.rightEyeCenter,
        rightEyeRadiusX: nextRig.rightEyeRadiusX,
        rightEyeRadiusY: nextRig.rightEyeRadiusY
      })
      nextRig.mouthRadiusY = newMouthRadiusY
      nextRig.expressionWeights = expr
    }
  } catch {}
  const nextLayers = await buildAvatarHeadLayer({
    avatarImage: nextImage,
    avatarRig: nextRig,
    settings,
    hairSegmenterImage,
    debug
  })
  if (!nextLayers) {
    debug && debug('[refreshAvatarAssets] buildAvatarHeadLayer failed; using fallback layers')
    const featureWrapProfile = nextRig ? buildFeatureWrapProfile(nextRig.canonical, nextRig) : null
    const layers = { _fallback: true }
    return {
      changed: true,
      nextAvatarSrc,
      nextImage,
      nextRig,
      nextLayers: layers,
      featureWrapProfile
    }
  }

  const featureWrapProfile = nextRig ? buildFeatureWrapProfile(nextRig.canonical, nextRig) : null
  return { changed: true, nextAvatarSrc, nextImage, nextRig, nextLayers, featureWrapProfile }
}
